import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  NgZone, OnDestroy, ViewChild
} from '@angular/core';
import {Router} from '@angular/router';
import L from 'leaflet';
import 'leaflet.markercluster';
import {NgrxBusy, withBusy} from 'ngrx-busy';
import {CollarLocation, DevicesClient, GetCollarLocations} from 'shared';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-collar-map',
  templateUrl: './collar-map.component.html',
  styleUrls: ['./collar-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CollarMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('map', {static: true}) private mapElement: ElementRef<HTMLDivElement>;
  @ViewChild('countControl', {static: true}) private countElement: ElementRef<HTMLDivElement>;
  @ViewChild(NgrxBusy, {static: true}) private busy: NgrxBusy;

  private readonly unsubscribe$ = new Subject<void>();
  private readonly activityLevels = new WeakMap<L.Marker, number>();
  private map: L.Map;
  private markers: L.MarkerClusterGroup;
  private resizeObserver: ResizeObserver;
  private snapshotAt: number;

  loading = false;
  hasLoaded = false;
  error: string | null = null;
  count = 0;

  constructor(private readonly client: DevicesClient,
              private readonly router: Router,
              private readonly zone: NgZone,
              private readonly cdr: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.map = L.map(this.mapElement.nativeElement, {
        center: [20, 0],
        zoom: 2,
        minZoom: 2,
        maxZoom: 20,
        worldCopyJump: true
      });

      L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
        id: 'yurysch/clghbzbig005101qud4lv91a7',
        tileSize: 512,
        zoomOffset: -1,
        accessToken: 'pk.eyJ1IjoieXVyeXNjaCIsImEiOiJjbDFlMnJscGowNnZrM2VydG95dDJucGkyIn0.gB-3c90Gp8o3scq_cWIj3g',
        maxNativeZoom: 19,
        maxZoom: 20,
        attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(this.map);

      this.markers = L.markerClusterGroup({
        maxClusterRadius: 60,
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        iconCreateFunction: cluster => this.createClusterIcon(cluster)
      }).addTo(this.map);

      const countControl = new L.Control({position: 'topright'});
      countControl.onAdd = () => {
        const element = this.countElement.nativeElement;
        L.DomEvent.disableClickPropagation(element);
        L.DomEvent.disableScrollPropagation(element);
        return element;
      };
      countControl.addTo(this.map);

      this.resizeObserver = new ResizeObserver(() => this.map.invalidateSize({pan: false}));
      this.resizeObserver.observe(this.mapElement.nativeElement);
    });

    this.refresh();
    this.cdr.detectChanges();
  }

  refresh(): void {
    if (this.loading) return;

    this.loading = true;
    this.error = null;
    this.client.getCollarLocations(new GetCollarLocations())
      .pipe(
        withBusy(() => this.busy),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: collars => {
          this.zone.runOutsideAngular(() => this.showCollars(collars));
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = 'Could not load collar locations. Please try again.';
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.resizeObserver?.disconnect();
    this.map?.remove();
  }

  private showCollars(collars: CollarLocation[]): void {
    this.snapshotAt = Date.now();
    const markers = collars
      .filter(collar => collar.location && Number.isFinite(collar.location.lat) && Math.abs(collar.location.lat) <= 90
        && Number.isFinite(collar.location.lng) && Math.abs(collar.location.lng) <= 180)
      .map(collar => this.createMarker(collar));

    this.markers.clearLayers();
    this.markers.addLayers(markers);
    this.count = markers.length;

    if (!this.hasLoaded && markers.length) {
      this.map.fitBounds(this.markers.getBounds(), {padding: [40, 40], maxZoom: 15});
    }
    this.hasLoaded = true;
  }

  private activityLevel(lastActive: number | undefined): number {
    if (!Number.isFinite(lastActive)) return 2;

    const daysSinceContact = (this.snapshotAt - lastActive) / 86_400_000;
    return daysSinceContact > 60 ? 2 : daysSinceContact > 20 ? 1 : 0;
  }

  private createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
    const markers = cluster.getAllChildMarkers();
    const total = markers.reduce((sum, marker) => sum + (this.activityLevels.get(marker) ?? 2), 0);
    const level = markers.length ? Math.round(total / markers.length) : 0;
    const color = ['green', 'yellow', 'red'][level];

    return L.divIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className: `collar-map-cluster collar-map-${color}`,
      iconSize: [48, 48]
    });
  }

  private createMarker(collar: CollarLocation): L.Marker {
    const level = this.activityLevel(collar.last_active?.valueOf());
    const color = ['green', 'yellow', 'red'][level];
    const avatar = document.createElement('img');
    avatar.loading = 'lazy';
    avatar.src = collar.pet.avatar
      ? `${collar.pet.avatar}${collar.pet.avatar.includes('?') ? '&' : '?'}size=small&scale=2`
      : '/assets/images/pet.png';
    avatar.alt = collar.pet.alias;
    avatar.addEventListener('error', () => avatar.src = '/assets/images/pet.png', {once: true});

    const marker = L.marker([collar.location.lat, collar.location.lng], {
      title: collar.pet.alias,
      alt: `Open ${collar.pet.alias}`,
      icon: L.divIcon({
        html: avatar,
        className: `collar-map-avatar collar-map-${color}`,
        iconSize: [48, 48],
        iconAnchor: [24, 24],
        tooltipAnchor: [0, -28]
      })
    });
    this.activityLevels.set(marker, level);

    const tooltip = document.createElement('div');
    const name = document.createElement('strong');
    name.textContent = collar.pet.alias;
    tooltip.appendChild(name);
    for (const [label, value] of [
      ['Owner', collar.owner.email || 'Unknown'],
      ['Last contact', collar.last_active?.local().format('LL LTS Z') || 'Unknown'],
      ['Location updated', collar.location_updated_at?.local().format('LL LTS Z') || 'Unknown'],
      ['Battery', collar.battery_charge != null ? `${collar.battery_charge}%` : 'Unknown']
    ]) {
      const row = document.createElement('div');
      row.textContent = `${label}: ${value}`;
      tooltip.appendChild(row);
    }

    marker.bindTooltip(tooltip, {direction: 'top', className: 'collar-map-tooltip'});
    marker.on('click', () => this.zone.run(() =>
      this.router.navigate(['/customers', collar.owner.id, 'pets', collar.pet.id])));
    return marker;
  }
}
